import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import { apiFetch } from '@/lib/operator-api/base-client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Users,
  Plus,
  Search,
  Loader2,
  UserCircle,
  Mail,
  Phone,
  Briefcase,
  Calendar,
  Edit3,
  Trash2,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

interface StaffMember {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: 'OWNER' | 'STAFF';
  active: boolean;
  color: string | null;
  avatarUrl: string | null;
  displayOrder: number;
  servicesCount: number;
  bookingsCount: number;
  createdAt: string;
}

interface StaffForm {
  name: string;
  email: string;
  phone: string;
  role: 'OWNER' | 'STAFF';
  color: string;
}

const STAFF_COLORS = [
  '#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444',
  '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16',
];

const DEFAULT_FORM: StaffForm = { name: '', email: '', phone: '', role: 'STAFF', color: '#10B981' };

// ============================================================================
// Main Page
// ============================================================================

function StaffPage() {
  const { t } = useTranslation();
  const { operator, isLoading: authLoading, selectedBusiness } = useOperator();
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<StaffForm>(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const businessId = selectedBusiness?.id;

  const fetchStaff = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const url = `/api/operator/staff?businessId=${businessId}&active=${!showInactive}`;
      const res = await apiFetch(url);
      const data = await res.json();
      if (data.success) setStaff(data.data || []);
    } catch (err) {
      console.error('Failed to load staff:', err);
    } finally {
      setLoading(false);
    }
  }, [businessId, showInactive]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...DEFAULT_FORM, color: STAFF_COLORS[staff.length % STAFF_COLORS.length] });
    setDialogOpen(true);
  };

  const openEdit = (member: StaffMember) => {
    setEditingId(member.id);
    setForm({
      name: member.name,
      email: member.email || '',
      phone: member.phone || '',
      role: member.role,
      color: member.color || '#10B981',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!businessId || !form.name.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await apiFetch(`/api/operator/staff/${editingId}?businessId=${businessId}`, {
          method: 'PATCH',
          body: JSON.stringify({
            name: form.name.trim(),
            email: form.email.trim() || null,
            phone: form.phone.trim() || null,
            role: form.role,
            color: form.color,
          }),
        });
      } else {
        await apiFetch(`/api/operator/staff?businessId=${businessId}`, {
          method: 'POST',
          body: JSON.stringify({
            businessId,
            name: form.name.trim(),
            email: form.email.trim() || undefined,
            phone: form.phone.trim() || undefined,
            role: form.role,
            color: form.color,
          }),
        });
      }
      setDialogOpen(false);
      setForm(DEFAULT_FORM);
      setEditingId(null);
      await fetchStaff();
    } catch (err) {
      console.error('Failed to save staff member:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (member: StaffMember) => {
    if (!businessId) return;
    try {
      await apiFetch(`/api/operator/staff/${member.id}?businessId=${businessId}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: !member.active }),
      });
      await fetchStaff();
    } catch (err) {
      console.error('Failed to toggle staff active:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!businessId) return;
    try {
      await apiFetch(`/api/operator/staff/${id}?businessId=${businessId}`, {
        method: 'DELETE',
      });
      setDeleteConfirm(null);
      await fetchStaff();
    } catch (err) {
      console.error('Failed to delete staff member:', err);
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!operator || !selectedBusiness) {
    return (
      <div className="p-6">
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto mb-4 text-amber-500" />
            <h3 className="text-lg font-semibold text-amber-800 mb-2">
              {t('settings.profile.noBusinessSelected')}
            </h3>
          </CardContent>
        </Card>
      </div>
    );
  }

  const filtered = staff.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.email?.toLowerCase().includes(search.toLowerCase()) ||
    s.phone?.includes(search)
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t('staff.title')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('staff.subtitle')}
            <span className="ml-2 text-primary">- {selectedBusiness.name}</span>
          </p>
        </div>
        <Button
          className="gap-2 bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white"
          onClick={openCreate}
        >
          <Plus className="h-4 w-4" />
          {t('staff.addMember')}
        </Button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4">
        <div className="flex items-center gap-3 p-4 rounded-xl bg-[hsl(160,84%,39%)]/5 border border-[hsl(160,84%,39%)]/20">
          <Users className="h-5 w-5 text-[hsl(160,84%,39%)]" />
          <div>
            <div className="text-xl font-bold">{staff.length}</div>
            <div className="text-xs text-muted-foreground">{t('staff.totalMembers')}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 p-4 rounded-xl bg-blue-500/5 border border-blue-500/20">
          <Briefcase className="h-5 w-5 text-blue-500" />
          <div>
            <div className="text-xl font-bold">{staff.filter((s) => s.role === 'OWNER').length}</div>
            <div className="text-xs text-muted-foreground">{t('staff.owners')}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 p-4 rounded-xl bg-purple-500/5 border border-purple-500/20">
          <Calendar className="h-5 w-5 text-purple-500" />
          <div>
            <div className="text-xl font-bold">{staff.reduce((sum, s) => sum + s.bookingsCount, 0)}</div>
            <div className="text-xs text-muted-foreground">{t('staff.totalBookings')}</div>
          </div>
        </div>
      </div>

      {/* Search + filter bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('staff.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          variant={showInactive ? 'default' : 'outline'}
          size="sm"
          className="gap-2 h-9"
          onClick={() => setShowInactive(!showInactive)}
        >
          {showInactive ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
          {t('staff.showInactive')}
        </Button>
      </div>

      {/* Staff Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[hsl(160,60%,18%)]" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <UserCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-semibold mb-1">
              {search ? t('staff.noResults') : t('staff.noStaff')}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {search
                ? t('staff.tryDifferentSearch')
                : t('staff.addFirstMember')}
            </p>
            {!search && (
              <Button className="gap-2" onClick={openCreate}>
                <Plus className="h-4 w-4" />
                {t('staff.addMember')}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12" />
                <TableHead>{t('staff.name')}</TableHead>
                <TableHead>{t('staff.contact')}</TableHead>
                <TableHead>{t('staff.role')}</TableHead>
                <TableHead className="text-center">{t('staff.services')}</TableHead>
                <TableHead className="text-center">{t('staff.bookings')}</TableHead>
                <TableHead>{t('staff.status')}</TableHead>
                <TableHead className="text-right">{t('staff.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((member) => (
                <TableRow key={member.id} className="group">
                  {/* Color dot */}
                  <TableCell>
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: member.color || '#94a3b8' }}
                    >
                      {member.name.charAt(0).toUpperCase()}
                    </div>
                  </TableCell>
                  {/* Name */}
                  <TableCell>
                    <div className="font-medium">{member.name}</div>
                  </TableCell>
                  {/* Contact */}
                  <TableCell>
                    <div className="space-y-0.5">
                      {member.email && (
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Mail className="h-3.5 w-3.5" />
                          {member.email}
                        </div>
                      )}
                      {member.phone && (
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Phone className="h-3.5 w-3.5" />
                          {member.phone}
                        </div>
                      )}
                      {!member.email && !member.phone && (
                        <span className="text-sm text-muted-foreground/50 italic">-</span>
                      )}
                    </div>
                  </TableCell>
                  {/* Role */}
                  <TableCell>
                    <Badge variant={member.role === 'OWNER' ? 'default' : 'secondary'}>
                      {member.role}
                    </Badge>
                  </TableCell>
                  {/* Services count */}
                  <TableCell className="text-center">{member.servicesCount}</TableCell>
                  {/* Bookings count */}
                  <TableCell className="text-center">{member.bookingsCount}</TableCell>
                  {/* Status */}
                  <TableCell>
                    {member.active ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{t('staff.active')}</Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">{t('staff.inactive')}</Badge>
                    )}
                  </TableCell>
                  {/* Actions */}
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => openEdit(member)}
                        title={t('common.edit')}
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => handleToggleActive(member)}
                        title={member.active ? t('staff.deactivate') : t('staff.activate')}
                      >
                        {member.active
                          ? <ToggleRight className="h-4 w-4 text-emerald-600" />
                          : <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                        }
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 hover:text-red-600"
                        onClick={() => setDeleteConfirm(member.id)}
                        title={t('common.delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) { setDialogOpen(false); setEditingId(null); } }}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>
              {editingId ? t('staff.editMember') : t('staff.addMember')}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? t('staff.editDesc')
                : t('staff.addDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>{t('staff.name')} *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Maria Silva"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{t('staff.email')}</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="maria@example.com"
                />
              </div>
              <div className="grid gap-2">
                <Label>{t('staff.phone')}</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+55 11 99999-0000"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{t('staff.role')}</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={form.role === 'STAFF' ? 'default' : 'outline'}
                    size="sm"
                    className="flex-1"
                    onClick={() => setForm({ ...form, role: 'STAFF' })}
                  >
                    {t('staff.staffRole')}
                  </Button>
                  <Button
                    type="button"
                    variant={form.role === 'OWNER' ? 'default' : 'outline'}
                    size="sm"
                    className="flex-1"
                    onClick={() => setForm({ ...form, role: 'OWNER' })}
                  >
                    {t('staff.ownerRole')}
                  </Button>
                </div>
              </div>
              <div className="grid gap-2">
                <Label>{t('staff.color')}</Label>
                <div className="flex gap-1.5 flex-wrap">
                  {STAFF_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`w-7 h-7 rounded-full transition-all ${
                        form.color === c ? 'ring-2 ring-offset-2 ring-foreground scale-110' : 'hover:scale-110'
                      }`}
                      style={{ backgroundColor: c }}
                      onClick={() => setForm({ ...form, color: c })}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !form.name.trim()}
              className="bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingId ? t('common.save') : t('staff.addMember')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{t('staff.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('staff.deleteDesc')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// Route Export
// ============================================================================

export const Route = createFileRoute('/_auth/operator/staff')({
  component: StaffPage,
});
